import React from 'react';
import PropTypes from 'prop-types';
// @material-ui/core components
import withStyles from '@material-ui/core/styles/withStyles';
import Avatar from '@material-ui/core/Avatar';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import Typography from '@material-ui/core/Typography';
// @material-ui/icons
// core components
import GridContainer from 'components/Grid/GridContainer.jsx';
import GridItem from 'components/Grid/GridItem.jsx';
import Button from 'components/CustomButtons/Button.jsx';
import CustomInput from 'components/CustomInput/CustomInput.jsx';
import Clearfix from 'components/Clearfix/Clearfix.jsx';
import Card from 'components/Card/Card.jsx';
import CardBody from 'components/Card/CardBody.jsx';
import CardHeader from 'components/Card/CardHeader.jsx';

import userProfileStyles from 'assets/jss/material-dashboard-pro-react/views/userProfileStyles.jsx';

import avatarPlaceholder from '../../../assets/img/avatar_placeholder.png';
import { compose, graphql, Query } from 'react-apollo';
import EmailVerificationChip from './EmailVerificationChip';
import { sendVerificationEmail } from '../../../auth/schema/auth-schema.graphql';
import {
  getUser,
  updateUserProfile,
  deleteUser,
} from '../schema/user-schema.graphql';

import selectStyles from 'assets/jss/material-dashboard-pro-react/customSelectStyle.jsx';
import Spinner from '../../../components/Spinner/Spinner';
import { withSnackbar } from '../../snackbar/SnackbarContext';
import DeleteUserButton from './DeleteUserButton';
import Switch from '@material-ui/core/Switch';
import localization from '../../../i18n/localization';
import genders from '../constants/genders';
import * as countries from '../constants/countries.json';

class UserDetails extends React.Component {
  static propTypes = {
    user: PropTypes.object.isRequired,
  };

  static defaultProps = {
    user: {
      email: '',
      firstName: '',
      lastName: '',
      phone: '',
      locale: '',
      country: '',
      gender: genders.MALE,
      newsletter: false,
    },
  };

  constructor(props) {
    super(props);
    const {
      email,
      firstName,
      lastName,
      phone,
      locale,
      country,
      gender,
      newsletter,
    } = props.user;
    this.state = {
      email: email,
      firstName: firstName,
      lastName: lastName,
      phone: phone,
      locale: locale,
      deleting: false,
      country: country,
      gender: gender,
      newsletter: newsletter,
      countryOptions: countries,
    };
  }

  inputChangeHandler = key => event => {
    this.setState({ [key]: event.target.value });
  };

  sendVerificationEmail = async () => {
    const { sendVerificationEmail, addNotification } = this.props;
    const { email } = this.state;
    const { data } = await sendVerificationEmail({ variables: { email } });
    if (data.sent) {
      addNotification('success', 'Verifizierungs Email versendet');
    } else {
      addNotification('error', 'Fehler beim senden der verfifizierungs Email');
    }
  };

  updateUserProfile = async () => {
    const { updateUserProfile, addNotification } = this.props;
    const {
      email,
      firstName,
      lastName,
      phone,
      locale,
      country,
      gender,
      newsletter,
    } = this.state;
    const { data } = await updateUserProfile({
      variables: {
        email,
        firstName,
        lastName,
        phone,
        locale,
        country,
        gender,
        newsletter,
      },
    });
    if (data.error) {
      addNotification('error', 'Fehler beim aktualisieren des Benutzers');
    } else {
      addNotification('success', 'Benutzer aktualisiert');
    }
  };

  deleteUser = async () => {
    const { deleteUser, addNotification, user } = this.props;
    this.setState({ deleting: true });

    try {
      const response = await deleteUser({
        variables: {
          userId: user.id,
        },
      });
      if (response.error) {
        addNotification('danger', 'Fehler beim anonymisieren des Benutzers');
      } else {
        addNotification('success', 'Benutzer anonymisiert');
      }
    } catch (error) {
      addNotification('danger', 'Fehler beim anonymisieren des Benutzers');
      console.error('error', error);
    }
    this.setState({ deleting: false });
  };

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.user.email !== this.props.user.email) {
      const {
        email,
        firstName,
        lastName,
        phone,
        locale,
        country,
        gender,
        newsletter,
      } = this.props.user;
      this.setState({
        email,
        firstName: firstName || '',
        lastName: lastName || '',
        phone: phone || '',
        locale,
        country: country || '',
        gender: gender || '',
        newsletter: newsletter || false,
      });
    }
  }

  toggleNewsletter = () => {
    this.setState(prevState => ({
      ...prevState,
      newsletter: !prevState.newsletter,
    }));
  };

  render() {
    const {
      firstName,
      lastName,
      phone,
      avatar,
      locale,
      deleting,
      country,
      gender,
      newsletter,
    } = this.state;
    const { classes, loading, user } = this.props;
    return (
      <GridItem xs={12} sm={12} md={12} lg={12}>
        <Card>
          <CardHeader image>
            <Avatar
              src={avatar || avatarPlaceholder}
              style={{ width: 80, height: 80 }}
            />

            <div
              style={{
                marginTop: 20,
                display: 'flex',
                width: '100%',
                alignItems: 'flex-start',
              }}
            >
              <div style={{ flex: 1 }}>
                <Typography variant="headline" color="textSecondary">
                  {user.email}
                </Typography>
              </div>
              {!loading && !user.anonymized && user ? (
                <div style={{ marginRight: 20 }}>
                  <EmailVerificationChip
                    onResendEmailVerification={this.sendVerificationEmail}
                    emailVerified={user.emailVerified}
                  />
                </div>
              ) : null}
            </div>
          </CardHeader>
          <CardBody>
            {loading ? (
              <Spinner loading />
            ) : (
              <div>
                <GridContainer>
                  <GridItem xs={12} sm={12} md={4} lg={4}>
                    <CustomInput
                      labelText="Vorname"
                      inputProps={{
                        value: firstName,
                        onChange: this.inputChangeHandler('firstName'),
                        disabled: user.anonymized,
                      }}
                      formControlProps={{
                        fullWidth: true,
                      }}
                    />
                  </GridItem>
                  <GridItem xs={12} sm={12} md={4} lg={4}>
                    <CustomInput
                      labelText="Nachname"
                      inputProps={{
                        value: lastName,
                        onChange: this.inputChangeHandler('lastName'),
                        disabled: user.anonymized,
                      }}
                      formControlProps={{
                        fullWidth: true,
                      }}
                    />
                  </GridItem>
                  {/*<GridItem xs={12} sm={12} md={6} lg={6}>*/}
                  {/*<CustomInput*/}
                  {/*labelText="Telefon"*/}
                  {/*inputProps={{*/}
                  {/*value: phone,*/}
                  {/*onChange: this.inputChangeHandler('phone'),*/}
                  {/*}}*/}
                  {/*formControlProps={{*/}
                  {/*fullWidth: true,*/}
                  {/*}}*/}
                  {/*/>*/}
                  {/*</GridItem>*/}
                  <GridItem xs={12} sm={12} md={4} lg={4}>
                    <FormControl
                      fullWidth
                      className={classes.selectFormControl}
                    >
                      <InputLabel
                        htmlFor="locale-select"
                        className={classes.selectLabel}
                      >
                        Sprache
                      </InputLabel>
                      <Select
                        MenuProps={{
                          className: classes.selectMenu,
                        }}
                        classes={{
                          select: classes.select,
                        }}
                        value={locale}
                        onChange={this.inputChangeHandler('locale')}
                        inputProps={{
                          name: 'locale-select',
                          id: 'locale-select',
                        }}
                        disabled={user.anonymized}
                      >
                        <MenuItem
                          classes={{
                            root: classes.selectMenuItem,
                            selected: classes.selectMenuItemSelected,
                          }}
                          value="DE"
                        >
                          DE
                        </MenuItem>
                        <MenuItem
                          classes={{
                            root: classes.selectMenuItem,
                            selected: classes.selectMenuItemSelected,
                          }}
                          value="EN"
                        >
                          EN
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </GridItem>
                </GridContainer>
                <GridContainer>
                  <GridItem xs={12} sm={12} md={4} lg={4}>
                    <FormControl
                      fullWidth
                      className={classes.selectFormControl}
                    >
                      <InputLabel
                        htmlFor="country-select"
                        className={classes.selectLabel}
                      >
                        {localization.de.user.country}
                      </InputLabel>
                      <Select
                        MenuProps={{
                          className: classes.selectMenu,
                        }}
                        classes={{
                          select: classes.select,
                        }}
                        value={country}
                        onChange={this.inputChangeHandler('country')}
                        inputProps={{
                          name: 'country-select',
                          id: 'country-select',
                        }}
                        disabled={user.anonymized}
                      >
                        {Object.values(this.state.countryOptions || []).map(
                          option => (
                            <MenuItem
                              key={option.value}
                              classes={{
                                root: classes.selectMenuItem,
                                selected: classes.selectMenuItemSelected,
                              }}
                              value={option.label}
                            >
                              {option.label}
                            </MenuItem>
                          )
                        ) || null}
                      </Select>
                    </FormControl>
                  </GridItem>
                  <GridItem xs={12} sm={12} md={4} lg={4}>
                    <FormControl
                      fullWidth
                      className={classes.selectFormControl}
                    >
                      <InputLabel
                        htmlFor="gender-select"
                        className={classes.selectLabel}
                      >
                        {localization.de.user.gender}
                      </InputLabel>
                      <Select
                        MenuProps={{
                          className: classes.selectMenu,
                        }}
                        classes={{
                          select: classes.select,
                        }}
                        value={gender}
                        onChange={this.inputChangeHandler('gender')}
                        inputProps={{
                          name: 'gender-select',
                          id: 'gender-select',
                        }}
                        disabled={user.anonymized}
                      >
                        <MenuItem
                          classes={{
                            root: classes.selectMenuItem,
                            selected: classes.selectMenuItemSelected,
                          }}
                          value={genders.MALE}
                        >
                          {localization.de.user.MALE}
                        </MenuItem>
                        <MenuItem
                          classes={{
                            root: classes.selectMenuItem,
                            selected: classes.selectMenuItemSelected,
                          }}
                          value={genders.FEMALE}
                        >
                          {localization.de.user.FEMALE}
                        </MenuItem>
                        <MenuItem
                          classes={{
                            root: classes.selectMenuItem,
                            selected: classes.selectMenuItemSelected,
                          }}
                          value={genders.OTHER}
                        >
                          {localization.de.user.OTHER}
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </GridItem>
                  <GridItem xs={12} sm={12} md={4} lg={4}>
                    <FormControl fullWidth={true}>
                      <InputLabel
                        htmlFor={localization.de.newsletter}
                        className={classes.label}
                      >
                        {localization.de.user.newsletter}
                      </InputLabel>
                    </FormControl>
                    <div style={{ marginTop: 20 }} />
                    <FormControl fullWidth={true}>
                      <Switch
                        onChange={this.toggleNewsletter}
                        checked={newsletter}
                        color={'primary'}
                      />
                    </FormControl>
                  </GridItem>
                </GridContainer>
                {!user.anonymized && (
                  <div
                    style={{
                      marginTop: 40,
                      display: 'flex',
                      flexDirection: 'row-reverse',
                    }}
                  >
                    <Button
                      style={{ marginLeft: 20 }}
                      color="info"
                      // className={classes.updateProfileButton}
                      onClick={this.updateUserProfile}
                      disabled={deleting}
                    >
                      Aktualisieren
                    </Button>
                    <DeleteUserButton
                      loading={deleting}
                      onDeleteUser={() => this.deleteUser(user.id)}
                    />
                  </div>
                )}
                <Clearfix />
              </div>
            )}
          </CardBody>
        </Card>
      </GridItem>
    );
  }
}

const enhance = compose(
  graphql(sendVerificationEmail, { name: 'sendVerificationEmail' }),
  graphql(updateUserProfile, { name: 'updateUserProfile' }),
  graphql(deleteUser, { name: 'deleteUser' }),
  withStyles({ ...userProfileStyles, ...selectStyles }),
  withSnackbar
);

const UserDetailsContainer = ({ userId, ...rest }) => (
  <Query query={getUser} variables={{ id: userId }}>
    {({ loading, data }) =>
      data ? <UserDetails loading={loading} user={data.user} {...rest} /> : null
    }
  </Query>
);

export default enhance(UserDetailsContainer);



// WEBPACK FOOTER //
// ./src/views/users/components/UserProfile.jsx